import { onlineManager, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import LangEnum from '../../../../../../../../lang/LangEnum';
import orderProductPacksRepository from '../../../../../../../../services/modules/order-product-packs/order-product-packs.repository';
import { getOrderSummaryQueryKey } from '../../../../../../../../services/modules/order/order.keys';
import { getFindProductGridKey } from '../../../../../../../../services/modules/product-grid/product-grid.keys';
import snackbarStore from '../../../../../../../../stores/Snackbar.store';
import { useApplicationContext } from '../../../../../../../context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../../../../../../../translation/TranslationContext';
import { validateOrderProductsQuery } from '../../../../../../contexts/order.utils';
import { getOrderCalculateUrlParams } from '../../../../../../utils/orderCalculate.utils';
import {
  IPackToUpdate,
  UseHandleOrderProductPacksProps,
} from '../types/useHandleOrderProductPacks.types';

const useHandleOrderProductPacks = ({
  orderId,
  availability,
  productColorId,
  deliveryWindowId,
}: UseHandleOrderProductPacksProps) => {
  const { team } = useApplicationContext();
  const queryClient = useQueryClient();
  const { handleMessage } = snackbarStore;
  const { translate } = useTranslation();
  const location = useLocation();

  const refetchProductGridQuery = () => {
    const queryKey = getFindProductGridKey({
      productColorId,
      availability,
      deliveryWindowId,
      orderId,
      isOfflineMode: !onlineManager.isOnline(),
    });

    queryClient.refetchQueries({
      type: 'active',
      queryKey: [team?.id, ...queryKey],
    });
  };

  const refetchOrderCalculateQuery = () => {
    if (!orderId) {
      throw new Error('order id not provided to refetch order calculate');
    }

    const { urlParams } = getOrderCalculateUrlParams(location.search);
    const queryKey = getOrderSummaryQueryKey({
      orderId,
      urlParams: urlParams?.toString(),
      isOfflineMode: !onlineManager.isOnline(),
    });
    queryClient.refetchQueries({
      type: 'active',
      queryKey: [team?.id, ...queryKey],
    });
  };

  const refetchValidateOrderProductsQuery = () => {
    const queryParams = validateOrderProductsQuery(orderId, team?.id);
    queryClient.refetchQueries(queryParams);
  };

  const refetchOrderQueries = async () => {
    refetchProductGridQuery();
    refetchOrderCalculateQuery();
    refetchValidateOrderProductsQuery();
  };

  const updatePacks = async (selectedPacks: IPackToUpdate[]) => {
    if (!orderId) {
      throw new Error('order id not provided to update packs');
    }

    const repository = orderProductPacksRepository({
      orderId,
      availability,
      productColorId,
      deliveryWindowId,
    });
    const response = await repository.updatePacks(selectedPacks);

    if (response.ok) {
      refetchOrderQueries();
    } else {
      handleMessage({
        type: 'error',
        message: translate(response.data?.code || LangEnum.ERROR_UPDATING_ITEM_MESSAGE),
      });
      refetchProductGridQuery();
    }

    return response;
  };

  return { updatePacks };
};

export default useHandleOrderProductPacks;
